import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const CheckoutSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useTheme();
  
  const orderConfirmation = location.state?.orderConfirmation;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const handleContinueShopping = () => {
    navigate('/cards');
  };

  const handleViewOrders = () => {
    // TODO: Implement order history page
    navigate('/profile');
  };

  if (!orderConfirmation) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" gutterBottom>
            Order Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            We couldn't find your order information.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/cards')}
            sx={{ px: 4, py: 1.5 }}
          >
            Continue Shopping
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Success Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <CheckCircleIcon 
          sx={{ 
            fontSize: 80, 
            color: 'success.main', 
            mb: 3 
          }} 
        />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
          Payment Successful!
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Thank you for your purchase. Your order has been confirmed.
        </Typography>
        
        <Alert severity="success" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          <Typography variant="body2">
            <strong>Order ID:</strong> {orderConfirmation.paymentIntentId}<br />
            <strong>Email confirmation sent to:</strong> {orderConfirmation.customerEmail}
          </Typography>
        </Alert>
      </Box>

      <Grid container spacing={4}>
        {/* Order Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Order Details
            </Typography>
            
            {orderConfirmation.items.map((item: any, index: number) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {item.card.player}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {item.card.year} {item.card.brand} {item.card.team}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Chip 
                        label={item.card.isGraded ? item.card.grade : item.card.condition} 
                        size="small" 
                        color={item.card.isGraded ? 'primary' : 'default'}
                      />
                      {item.card.isGraded && (
                        <Chip label="Graded" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Quantity: {item.quantity}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700,
                      color: mode === 'light' ? '#000000' : '#FFFFFF',
                      textAlign: 'right'
                    }}>
                      {formatPrice(item.card.price * item.quantity)}
                    </Typography>
                  </Grid>
                </Grid>
                {index < orderConfirmation.items.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Paper>

          {/* Shipping Information */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ShippingIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Shipping Information
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {orderConfirmation.shippingAddress.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {orderConfirmation.shippingAddress.line1}
              {orderConfirmation.shippingAddress.line2 && (
                <><br />{orderConfirmation.shippingAddress.line2}</>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orderConfirmation.shippingAddress.city}, {orderConfirmation.shippingAddress.state} {orderConfirmation.shippingAddress.postalCode}
            </Typography>
          </Paper>
        </Grid>

        {/* Order Summary & Next Steps */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 'fit-content', position: 'sticky', top: 20 }}>
            <Typography variant="h5" gutterBottom>
              Order Summary
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">{formatPrice(orderConfirmation.total)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping:</Typography>
                <Typography variant="body1">Free</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">Total:</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ 
                  color: mode === 'light' ? '#000000' : '#FFFFFF'
                }}>
                  {formatPrice(orderConfirmation.total)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              What's Next?
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2">
                  Email confirmation sent
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShippingIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2">
                  Processing your order
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                You'll receive tracking information once your order ships.
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleContinueShopping}
              sx={{ mb: 2, py: 1.5 }}
            >
              Continue Shopping
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={handleViewOrders}
              sx={{ py: 1.5 }}
            >
              View My Orders
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutSuccessPage;
